import $ from "jquery";
import { computePosition, shift } from "@floating-ui/dom";

interface NoteElement {
    noteElem: HTMLElement; // The note element to position
    anchorElem: HTMLElement; // The text element the note is linked to
    height: number; // Height of the note
    top: number; // Current top position
}

$(function () {
    /**
     * Position notes in the sidebar, avoiding overlaps
     */
    async function positionNotes() {
        const placedNotes: NoteElement[] = [];

        // Find all note elements and their corresponding anchors
        const noteElements = $("[data-note-target]")
            .map(function () {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const noteElem = this;
                const targetId = $(noteElem).data("note-target") as string | undefined;
                const anchorElem = $(`[data-note="${targetId}"]`)[0];

                if (!anchorElem) {
                    console.warn(`Anchor element with data-note="${targetId}" not found.`);
                    return null; // Skip this note if no anchor element is found
                }

                return {
                    noteElem,
                    anchorElem,
                    height: 0,
                    top: 0
                };
            })
            .get()
            .filter((note) => note !== null); // Filter out null values from the map

        // Reset positions before calculating
        noteElements.forEach((note) => {
            note.noteElem.style.top = "0px";
        });

        // Ensure DOM updates have been applied
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // Position each note
        for (const note of noteElements) {
            await positionNote(note, placedNotes);
        }
    }

    /**
     * Position a single note, handling overlaps with previously positioned notes
     */
    async function positionNote(note: NoteElement, placedNotes: NoteElement[]): Promise<void> {
        // Calculate initial position using floating-ui
        const initialPosition = await computePosition(note.anchorElem, note.noteElem, {
            middleware: [shift()]
        });

        // Get anchor element's position and dimensions
        const anchorRect = note.anchorElem.getBoundingClientRect();
        // Force layout recalculation to get accurate height
        note.height = note.noteElem.offsetHeight;
        // Calculate the top position based on the anchor element's position and the note's height
        if (note.noteElem.classList.contains("note-top")) {
            note.top = initialPosition.y - anchorRect.height;
        } else if (note.noteElem.classList.contains("note-bottom")) {
            note.top = initialPosition.y - note.height;
        } else if (note.noteElem.classList.contains("note-imageBottom")) {
            note.top = initialPosition.y;
        } else {
            note.top = initialPosition.y - (note.height / 2 + anchorRect.height / 2);
        }

        const MARGIN = 10; // px between notes

        // Check if this note would overlap with any previously placed notes
        let overlaps = false;
        let highestBottom = 0;

        for (const placedNote of placedNotes) {
            const placedNoteBottom = placedNote.top + placedNote.height;
            const noteBottom = note.top + note.height;

            // Check for overlap
            if (!(note.top >= placedNoteBottom || noteBottom <= placedNote.top)) {
                overlaps = true;
                highestBottom = Math.max(highestBottom, placedNoteBottom);
            }
        }

        // If overlap detected, position this note below the lowest placed note
        if (overlaps) {
            note.top = highestBottom + MARGIN;
        }

        note.noteElem.style.top = `${note.top}px`;

        // Add to the list of placed notes for future overlap checks
        placedNotes.push(note);
    }

    // Run initial positioning
    void positionNotes();

    // Update positions on resize
    let resizeTimer: number;
    $(window).on("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            void positionNotes();
        }, 150);
    });

    // Handle hover effects
    function toggleHoverClass(event: JQuery.TriggeredEvent) {
        const isHovering = event.type === "mouseenter";
        const noteId =
            ($(event.currentTarget).data("note-target") as string | undefined) ??
            ($(event.currentTarget).data("note") as string | undefined);

        $(`[data-note="${noteId}"], [data-note-target="${noteId}"]`).toggleClass("js-hovered", isHovering);
    }

    $("[data-note-target], [data-note]").on("mouseenter mouseleave", toggleHoverClass);

    // Add click handler for scrolling to corresponding elements
    $("[data-note]").on("click", function (event) {
        event.preventDefault();
        const noteId = $(this).data("note") as string;
        const targetElement = $(`[data-note-target="${noteId}"]`)[0];

        if (targetElement) {
            // Scroll the note into view with smooth behavior
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });

            // Add a temporary highlight effect
            $(targetElement).addClass("js-highlighted");
            setTimeout(() => {
                $(targetElement).removeClass("js-highlighted");
            }, 2500);
        }
    });
});
